import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  Plus,
  CreditCard,
  Pencil,
  Trash2,
  Download,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import Pagination from '@/components/Pagination'
import type { Payment, PaginationMeta } from '@/types'

interface Props {
  payments: Payment[]
  finalized_pos: { id: number; amount: string }[]
  filters: { linked: string | null }
  pagination: PaginationMeta
}

const emptyForm = {
  amount: '',
  date: new Date().toISOString().split('T')[0],
  notes: '',
  purchase_order_id: '',
}

export default function PaymentsIndex({ payments, finalized_pos, filters, pagination }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [processing, setProcessing] = useState(false)

  function handleFilterChange(value: string) {
    router.get(
      '/payments',
      { linked: value === 'all' ? null : value },
      { preserveState: true, preserveScroll: true }
    )
  }

  function openCreateDialog() {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  function openEditDialog(payment: Payment) {
    setEditingId(payment.id)
    setForm({
      amount: payment.amount,
      date: payment.date,
      notes: payment.notes || '',
      purchase_order_id: payment.purchase_order_id?.toString() || '',
    })
    setDialogOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)

    const payload = {
      payment: {
        ...form,
        purchase_order_id: form.purchase_order_id || null,
      },
    }

    if (editingId) {
      router.put(`/payments/${editingId}`, payload, {
        onFinish: () => {
          setProcessing(false)
          setDialogOpen(false)
        },
      })
    } else {
      router.post('/payments', payload, {
        onFinish: () => {
          setProcessing(false)
          setDialogOpen(false)
        },
      })
    }
  }

  function handleDelete(id: number) {
    if (confirm('Are you sure you want to delete this payment?')) {
      router.delete(`/payments/${id}`)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage all payments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={`/payments/csv${filters.linked ? `?linked=${filters.linked}` : ''}`} download>
              <Download className="size-4" />
              CSV
            </a>
          </Button>
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="size-4" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-border/50 mb-6">
        <CardContent className="flex items-center gap-4 pt-6">
          <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          <Select
            value={filters.linked || 'all'}
            onValueChange={handleFilterChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All payments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="linked">Linked to PO</SelectItem>
              <SelectItem value="unlinked">Unlinked</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Data table */}
      <Card className="border-border/50">
        <CardContent className="pt-0">
          {payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CreditCard className="size-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">No payments found</p>
              <p className="text-xs mt-1">Record a payment to get started</p>
              <Button size="sm" className="mt-4" onClick={openCreateDialog}>
                <Plus className="size-4" />
                Record Payment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="border-border/30">
                    <TableCell className="font-mono text-sm font-medium">
                      {formatCurrency(parseFloat(payment.amount))}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(payment.date)}
                    </TableCell>
                    <TableCell>
                      {payment.purchase_order_id ? (
                        <Link
                          href={`/purchase_orders/${payment.purchase_order_id}`}
                          className="text-primary hover:underline text-sm font-medium"
                        >
                          PO #{payment.purchase_order_id}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground/40 text-sm">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {payment.notes || '--'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => openEditDialog(payment)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDelete(payment.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination pagination={pagination} baseUrl="/payments" params={{ linked: filters.linked }} />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Payment' : 'Record Payment'}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update the payment details below.'
                : 'Enter the payment details below.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pay-amount">Amount</Label>
                <Input
                  id="pay-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pay-date">Date</Label>
                <Input
                  id="pay-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pay-po">Link to Purchase Order (optional)</Label>
              <Select
                value={form.purchase_order_id || 'none'}
                onValueChange={(value) =>
                  setForm({ ...form, purchase_order_id: value === 'none' ? '' : value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="No linked PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked PO</SelectItem>
                  {finalized_pos.map((po) => (
                    <SelectItem key={po.id} value={po.id.toString()}>
                      PO #{po.id} - {formatCurrency(parseFloat(po.amount))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pay-notes">Notes</Label>
              <Textarea
                id="pay-notes"
                placeholder="Add any relevant notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={processing}>
                {processing
                  ? 'Saving...'
                  : editingId
                    ? 'Save Changes'
                    : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
